import { useState } from 'react';
import { trpc } from '../lib/trpc.js';
import { formatGbp } from '../lib/format.js';
import { clsx } from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';

export function TechniciansPage() {
  const list = trpc.technicians.list.useQuery();
  const utils = trpc.useUtils();
  const upsert = trpc.technicians.upsert.useMutation({
    onSuccess: () => utils.technicians.list.invalidate(),
  });

  if (list.isLoading) return <p className="text-gray-500">Loading…</p>;
  if (!list.data) return <p className="text-gray-500">No data</p>;

  const techs = list.data;
  const unrated = techs.filter((t) => !t.excludeFromCost && (t.hourlyRateGbp === null || t.hourlyRateGbp === 0));
  const totalConfigured = techs.filter((t) => t.rateConfigured).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-navy">Technicians & rates</h1>
        <p className="text-sm text-gray-500">
          Per-hour cost for each technician. Cost rule: span of first job-start to last job-end on a given day × rate (covers travel-time pay between jobs).
        </p>
      </div>

      {unrated.length > 0 && (
        <div className="card border border-amber-200 bg-amber-50/60">
          <p className="text-sm">
            <strong>{unrated.length} technician{unrated.length === 1 ? '' : 's'}</strong> need an hourly rate set before labor cost can be computed: {' '}
            <span className="font-mono">{unrated.map((t) => t.name).join(', ')}</span>.
          </p>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-xs text-gray-500">
              <th className="text-left py-2 px-3 font-medium">Name</th>
              <th className="text-right py-2 px-3 font-medium">Jobs</th>
              <th className="text-left py-2 px-3 font-medium">Last job</th>
              <th className="text-right py-2 px-3 font-medium">£/hr</th>
              <th className="text-left py-2 px-3 font-medium">Email (for payslip)</th>
              <th className="text-center py-2 px-3 font-medium">Exclude</th>
              <th className="text-left py-2 px-3 font-medium">Notes</th>
              <th className="text-center py-2 px-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {techs.map((t) => (
              <TechRow
                key={t.name}
                tech={t}
                onSave={(values) => upsert.mutate({ name: t.name, ...values })}
                saving={upsert.isPending}
              />
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 mt-3">
          {totalConfigured} of {techs.length} technicians have rates configured.
        </p>
      </div>
    </div>
  );
}

function TechRow({
  tech,
  onSave,
  saving,
}: {
  tech: {
    name: string;
    email: string | null;
    hourlyRateGbp: number | null;
    excludeFromCost: boolean;
    notes: string | null;
    jobs: number;
    lastJobAt: Date | string | null;
    rateConfigured: boolean;
  };
  onSave: (v: { email: string | null; hourlyRateGbp: number | null; excludeFromCost: boolean; notes: string | null }) => void;
  saving: boolean;
}) {
  const [rateInput, setRateInput] = useState(tech.hourlyRateGbp !== null ? String(tech.hourlyRateGbp) : '');
  const [emailInput, setEmailInput] = useState(tech.email ?? '');
  const [excluded, setExcluded] = useState(tech.excludeFromCost);
  const [notes, setNotes] = useState(tech.notes ?? '');
  const [dirty, setDirty] = useState(false);

  const save = () => {
    const parsedRate = rateInput.trim() === '' ? null : parseFloat(rateInput);
    onSave({
      email: emailInput.trim() || null,
      hourlyRateGbp: parsedRate !== null && !isNaN(parsedRate) ? parsedRate : null,
      excludeFromCost: excluded,
      notes: notes.trim() || null,
    });
    setDirty(false);
  };

  return (
    <tr className="border-b border-gray-50 hover:bg-cream-tint">
      <td className="py-2 px-3 font-medium text-navy">{tech.name}</td>
      <td className="py-2 px-3 text-right font-mono">{tech.jobs}</td>
      <td className="py-2 px-3 text-xs text-gray-500">
        {tech.lastJobAt ? formatInTimeZone(new Date(tech.lastJobAt), 'Europe/London', 'd MMM yyyy') : '—'}
      </td>
      <td className="py-2 px-3 text-right">
        <input
          type="number"
          step="0.5"
          min="0"
          placeholder={excluded ? 'n/a' : '—'}
          disabled={excluded}
          value={rateInput}
          onChange={(e) => { setRateInput(e.target.value); setDirty(true); }}
          className={clsx(
            'w-20 px-2 py-1 rounded border text-right font-mono text-sm',
            excluded ? 'bg-gray-50 text-gray-400 border-gray-100' : 'border-gray-200',
          )}
        />
      </td>
      <td className="py-2 px-3">
        <input
          type="email"
          value={emailInput}
          disabled={excluded}
          onChange={(e) => { setEmailInput(e.target.value); setDirty(true); }}
          placeholder={excluded ? 'n/a' : 'tech@example.com'}
          className={clsx(
            'w-56 px-2 py-1 rounded border text-sm',
            excluded ? 'bg-gray-50 text-gray-400 border-gray-100' : 'border-gray-200',
          )}
        />
      </td>
      <td className="py-2 px-3 text-center">
        <input
          type="checkbox"
          checked={excluded}
          onChange={(e) => { setExcluded(e.target.checked); setDirty(true); }}
        />
      </td>
      <td className="py-2 px-3">
        <input
          type="text"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
          placeholder="optional"
          className="w-full px-2 py-1 rounded border border-gray-200 text-sm"
        />
      </td>
      <td className="py-2 px-3 text-center">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className={clsx(
            'px-3 py-1 rounded-md text-xs font-medium',
            dirty && !saving ? 'bg-navy text-white' : 'bg-gray-100 text-gray-400',
          )}
        >
          {saving ? '…' : 'Save'}
        </button>
      </td>
    </tr>
  );
}
